import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { User } from 'firebase/auth';

import {
  getAuthClaims,
  isLocalhostAuthHost,
  observeAuthState,
  recoverInvalidEmulatorSession,
  refreshAuthClaims,
  type AuthClaims,
} from '../../services/firebase/auth';
import { PendingApproval } from './PendingApproval';

const isAuthBypass = import.meta.env.VITE_BYPASS_AUTH === 'true';
const EMPTY_AUTH_CLAIMS: AuthClaims = {
  approved: false,
  admin: false,
};
const BYPASS_AUTH_CLAIMS: AuthClaims = {
  approved: true,
  admin: true,
};

export interface AuthOutletContext {
  authUser: User | null;
  authClaims: AuthClaims;
  isAuthBypass: boolean;
}

export function AuthGuard(): JSX.Element {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [authClaims, setAuthClaims] = useState<AuthClaims>(
    isAuthBypass ? BYPASS_AUTH_CLAIMS : EMPTY_AUTH_CLAIMS,
  );
  const [isResolving, setIsResolving] = useState(!isAuthBypass);
  const location = useLocation();
  const isLocalAnonymousUser =
    !isAuthBypass && authUser?.isAnonymous === true && isLocalhostAuthHost();

  useEffect(() => {
    if (isAuthBypass) {
      return;
    }
    let isDisposed = false;

    const unsubscribe = observeAuthState((user) => {
      setAuthUser(user);

      if (!user) {
        setAuthClaims(EMPTY_AUTH_CLAIMS);
        setIsResolving(false);
        return;
      }

      setIsResolving(true);
      void getAuthClaims(user)
        .then((nextClaims) => {
          if (isDisposed) {
            return;
          }

          setAuthClaims(nextClaims);
          setIsResolving(false);
        })
        .catch(async (error: unknown) => {
          console.warn('[auth] Failed to resolve access claims', error);
          if (isDisposed) {
            return;
          }

          if (await recoverInvalidEmulatorSession(error)) {
            if (isDisposed) {
              return;
            }
            setAuthUser(null);
          }

          setAuthClaims(EMPTY_AUTH_CLAIMS);
          setIsResolving(false);
        });
    });

    return () => {
      isDisposed = true;
      unsubscribe();
    };
  }, []);

  const handleRefreshApproval = useCallback(async (): Promise<void> => {
    if (!authUser || isAuthBypass) {
      return;
    }

    try {
      const nextClaims = await refreshAuthClaims(authUser);
      setAuthClaims(nextClaims);
    } catch (error) {
      console.warn('[auth] Failed to refresh approval', error);
      if (await recoverInvalidEmulatorSession(error)) {
        setAuthUser(null);
        setAuthClaims(EMPTY_AUTH_CLAIMS);
      }
    }
  }, [authUser]);

  useEffect(() => {
    if (!isLocalAnonymousUser || authClaims.approved || !authUser) {
      return;
    }

    let isDisposed = false;
    const intervalId = window.setInterval(() => {
      void refreshAuthClaims(authUser)
        .then((nextClaims) => {
          if (!isDisposed) {
            setAuthClaims(nextClaims);
          }
        })
        .catch(async (error: unknown) => {
          if (isDisposed) {
            return;
          }
          if (await recoverInvalidEmulatorSession(error)) {
            if (isDisposed) {
              return;
            }
            setAuthUser(null);
            setAuthClaims(EMPTY_AUTH_CLAIMS);
          }
        });
    }, 1_000);

    return () => {
      isDisposed = true;
      window.clearInterval(intervalId);
    };
  }, [authClaims.approved, authUser, isLocalAnonymousUser]);

  if (isResolving) {
    return <div className="flex h-full items-center justify-center">Loading...</div>;
  }

  if (!isAuthBypass && !authUser) {
    const redirect = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  if (isLocalAnonymousUser && !authClaims.approved) {
    return (
      <div className="flex h-full items-center justify-center">Starting local guest session...</div>
    );
  }

  if (!isAuthBypass && authUser && !authClaims.approved) {
    return <PendingApproval authUser={authUser} onRefreshApproval={handleRefreshApproval} />;
  }

  return <Outlet context={{ authUser, authClaims, isAuthBypass }} />;
}
